import React, { useState } from 'react';

const GICampaignsSelector = ({
  selectedClient,
  campaigns,
  campaignsLoading,
  selectedCampaigns,
  onCampaignsChange,
  maxSelectedCampaigns = 10,
  onLimitReached,
  compactMode = false, // Nouveau prop pour le mode compact
}) => {

  // State local pour contrôler l'affichage pliable de la section campagnes
  // Complètement isolé de l'URL pour éviter les re-renders du parent
  const [campaignsSectionCollapsed, setCampaignsSectionCollapsed] = useState(false);

  // Handler pour gérer l'état plié/déplié des campagnes
  const handleCampaignsSectionToggle = () => {
    setCampaignsSectionCollapsed(!campaignsSectionCollapsed);
  };

  // Handler pour sélectionner des campagnes
  const handleCampaignsSelection = (campaigns) => {
    onCampaignsChange(campaigns);
  };

  // Handler pour sélectionner/désélectionner une campagne spécifique
  const handleCampaignToggle = (campaign) => {
    const isSelected = selectedCampaigns.some(c => c.value === campaign.value);

    let newSelectedCampaigns;
    if (isSelected) {
      // Désélectionner
      newSelectedCampaigns = selectedCampaigns.filter(c => c.value !== campaign.value);
    } else {
      if (selectedCampaigns.length >= maxSelectedCampaigns) {
        onLimitReached?.(maxSelectedCampaigns);
        return;
      }

      // Sélectionner
      newSelectedCampaigns = [...selectedCampaigns, campaign];
    }

    handleCampaignsSelection(newSelectedCampaigns);
  };

  // Handler pour tout désélectionner
  const handleDeselectAll = () => {
    handleCampaignsSelection([]);
  };

  if (!selectedClient || campaigns.length === 0) {
    return null;
  }

    // Mode compact pour le layout à 2 colonnes
  if (compactMode) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header avec actions - fixed */}
        <div className="flex items-center justify-between mb-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            {selectedCampaigns.length > 0 && (
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {selectedCampaigns.length}/{maxSelectedCampaigns}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectedCampaigns.length > 0 && (
              <button
                onClick={handleDeselectAll}
                className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                title="Tout désélectionner"
              >
                Effacer
              </button>
            )}
          </div>
        </div>

        {/* Liste des campagnes avec scroll - flexible */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {campaignsLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div style={{ height: '100%', overflowY: 'auto' }} className="space-y-1.5">
              {campaigns.map((campaign) => {
                const isSelected = selectedCampaigns.some(c => c.value === campaign.value);
                const selectionBlocked = !isSelected && selectedCampaigns.length >= maxSelectedCampaigns;
                return (
                  <div
                    key={campaign.value}
                    onClick={() => handleCampaignToggle(campaign)}
                    className={`border rounded-md p-2.5 transition-all duration-200 flex-shrink-0 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : selectionBlocked
                          ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60 dark:border-gray-700 dark:bg-gray-800/60'
                          : 'cursor-pointer border-gray-200 dark:border-gray-600 hover:border-gray-300 hover:shadow-sm dark:hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="font-medium text-gray-900 dark:text-gray-100 text-xs leading-snug mb-1 line-clamp-2">
                          {campaign.data.CampaignName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-500 leading-tight">
                          {new Date(campaign.data.CampaignStartDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} - {' '}
                          {new Date(campaign.data.CampaignEndDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                        </div>
                      </div>

                      {/* Checkbox de sélection */}
                      <div className="flex-shrink-0">
                        <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {isSelected && (
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Mode normal (existant)
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            Campagnes disponibles ({campaigns.length})
          </h3>
          <button
            onClick={handleCampaignsSectionToggle}
            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            title={campaignsSectionCollapsed ? "Déplier" : "Replier"}
          >
            <svg
              className={`w-5 h-5 transition-transform duration-200 ${campaignsSectionCollapsed ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        {selectedCampaigns.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {selectedCampaigns.length}/{maxSelectedCampaigns} sélectionnée{selectedCampaigns.length > 1 ? 's' : ''}
            </span>
            <button
              onClick={handleDeselectAll}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              Tout désélectionner
            </button>
          </div>
        )}
      </div>

      {!campaignsSectionCollapsed && (
        campaignsLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Chargement des campagnes...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {campaigns.map((campaign) => {
              const isSelected = selectedCampaigns.some(c => c.value === campaign.value);
              const selectionBlocked = !isSelected && selectedCampaigns.length >= maxSelectedCampaigns;
              return (
                <div
                  key={campaign.value}
                  onClick={() => handleCampaignToggle(campaign)}
                  className={`relative border-2 rounded-lg p-4 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-sm'
                      : selectionBlocked
                        ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60 dark:border-gray-700 dark:bg-gray-800/60'
                        : 'cursor-pointer border-gray-200 hover:border-gray-300 hover:shadow-md dark:border-gray-600 dark:hover:border-gray-500'
                  }`}
                >
                  {/* Pastille de sélection */}
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* Contenu de la tuile */}
                  <div className="pr-6">
                    <div className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-1 line-clamp-2">
                      {campaign.data.CampaignName}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-500">
                      {new Date(campaign.data.CampaignStartDate).toLocaleDateString('fr-FR')} - {' '}
                      {new Date(campaign.data.CampaignEndDate).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
      {/* Affichage compacte quand la section est pliée */}
      {campaignsSectionCollapsed && (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {selectedCampaigns.length > 0 ? (
            <div>
              <div className="mb-1">
                {selectedCampaigns.length} campagne{selectedCampaigns.length > 1 ? 's' : ''} sélectionnée{selectedCampaigns.length > 1 ? 's' : ''} sur {campaigns.length}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-500">
                {selectedCampaigns.map(campaign => campaign.label).join(', ')}
              </div>
            </div>
          ) : (
            <span>
              {campaigns.length} campagne{campaigns.length > 1 ? 's' : ''} disponible{campaigns.length > 1 ? 's' : ''} - Cliquez pour déplier et sélectionner
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default GICampaignsSelector;